import type { Locale } from '../i18n/t.js';
import type { ProgressRepository } from '../storage/progress-repository.js';
import type { Attempt, Profile, Skill } from '../storage/types.js';
import { defaultProfile } from '../storage/types.js';
import {
  additionParams,
  countingParams,
  MAX_LEVEL,
  recognitionParams,
  subtractionParams,
  type AdditionLevelParams,
  type CountingLevelParams,
  type RecognitionLevelParams,
  type SubtractionLevelParams,
} from './levels.js';

export interface SessionResult {
  skill: Skill;
  correctCount: number;
  previousLevel: number;
  nextLevel: number;
  /** Promotion is celebrated. Demotion is silent. Both update level state. */
  promoted: boolean;
  demoted: boolean;
}

type Listener = (profile: Readonly<Profile>) => void;

const TRIALS_PER_SESSION = 5;

/**
 * Owns the in-memory profile. All progress mutations go through this class so
 * subscribers re-render when state changes. Persists via `ProgressRepository`
 * on every mutation.
 */
export class Progression {
  private profile: Profile;
  private repo: ProgressRepository;
  private listeners = new Set<Listener>();

  private constructor(repo: ProgressRepository, profile: Profile) {
    this.repo = repo;
    this.profile = profile;
  }

  static async create(repo: ProgressRepository): Promise<Progression> {
    const profile = await repo.load();
    return new Progression(repo, profile);
  }

  // ---------- Reads ----------

  getProfile(): Readonly<Profile> {
    return this.profile;
  }

  getLevel(skill: Skill): number {
    return this.profile.skills[skill].level;
  }

  getLocale(): Locale | null {
    return this.profile.locale;
  }

  recognitionParams(): RecognitionLevelParams {
    return recognitionParams(this.getLevel('recognition'));
  }

  countingParams(): CountingLevelParams {
    return countingParams(this.getLevel('counting'));
  }

  additionParams(): AdditionLevelParams {
    return additionParams(this.getLevel('addition'));
  }

  subtractionParams(): SubtractionLevelParams {
    return subtractionParams(this.getLevel('subtraction'));
  }

  // ---------- Mutations ----------

  async setLocale(locale: Locale): Promise<void> {
    this.profile.locale = locale;
    await this.persist();
  }

  async recordAttempt(skill: Skill, level: number, correct: boolean): Promise<void> {
    const attempt: Attempt = { skill, level, correct, timestamp: Date.now() };
    await this.repo.recordAttempt(attempt);
    // Reload the bounded history into the in-memory profile so subscribers
    // see consistent state.
    this.profile = await this.repo.load();
    this.notify();
  }

  /**
   * Apply the session-end evaluator.
   *
   *  - C ≥ 4         → promote (capped). Reset bad-session counter.
   *  - C ≤ 1, bad=0  → stay, set bad=1.
   *  - C ≤ 1, bad≥1  → demote (floored). Reset bad-session counter.
   *  - else (2..3)   → stay, reset bad-session counter.
   *
   * Demotion is silent: callers should NOT show any demotion-specific UI.
   */
  async recordSessionResult(skill: Skill, correctCount: number): Promise<SessionResult> {
    if (correctCount < 0 || correctCount > TRIALS_PER_SESSION) {
      throw new RangeError(`correctCount must be in [0, ${TRIALS_PER_SESSION}]`);
    }
    const state = this.profile.skills[skill];
    const previousLevel = state.level;
    let nextLevel = previousLevel;
    let promoted = false;
    let demoted = false;

    if (correctCount >= 4) {
      nextLevel = Math.min(previousLevel + 1, MAX_LEVEL[skill]);
      promoted = nextLevel > previousLevel;
      state.badSessionsAtLevel = 0;
    } else if (correctCount <= 1) {
      if (state.badSessionsAtLevel >= 1) {
        nextLevel = Math.max(previousLevel - 1, 1);
        demoted = nextLevel < previousLevel;
        state.badSessionsAtLevel = 0;
      } else {
        state.badSessionsAtLevel = 1;
      }
    } else {
      state.badSessionsAtLevel = 0;
    }

    if (nextLevel !== previousLevel) {
      state.level = nextLevel;
      // Reset hysteresis on any level change.
      state.badSessionsAtLevel = 0;
    }

    await this.persist();

    return { skill, correctCount, previousLevel, nextLevel, promoted, demoted };
  }

  async setLevel(skill: Skill, level: number): Promise<void> {
    const clamped = Math.min(Math.max(level, 1), MAX_LEVEL[skill]);
    const state = this.profile.skills[skill];
    if (state.level === clamped) return;
    state.level = clamped;
    state.badSessionsAtLevel = 0;
    await this.persist();
  }

  async reset(): Promise<void> {
    this.profile = defaultProfile();
    await this.persist();
  }

  // ---------- Subscriptions ----------

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async persist(): Promise<void> {
    await this.repo.save(this.profile);
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.profile);
  }
}
